import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Tag,
  Space,
  Divider,
  Button,
  Progress,
  Spin,
  Form,
  Input,
  Row,
  Col,
  Typography,
  Modal,
  Avatar,
  Table,
} from "antd";
import {
  FaProjectDiagram,
  FaPauseCircle,
  FaPlayCircle,
  FaCheckCircle,
  FaClock,
  FaArrowLeft,
  FaCalendarAlt,
  FaFlagCheckered,
  FaUsers,
  FaExclamationCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Api from "../../api/Api";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const calcularDiasSuspenso = (suspensoes) => {
  if (!Array.isArray(suspensoes) || suspensoes.length === 0) return 0;
  let totalDias = 0;
  suspensoes.forEach((s) => {
    if (s.entrada && s.saida) {
      const entrada = new Date(s.entrada);
      const saida = new Date(s.saida);
      const diff = Math.ceil((saida - entrada) / (1000 * 60 * 60 * 24));
      totalDias += diff;
    }
  });
  return totalDias;
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE SUSPENSÃO
───────────────────────────────────────────────────────────────────────────── */
const SuspenderModal = ({ open, onClose, onSuccess, projetoId }) => {
  const [descricao, setDescricao] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSuspender = async () => {
    if (!descricao.trim()) {
      toast.error("Informe o motivo da suspensão.");
      return;
    }

    try {
      setLoading(true);
      await Api.AlterStatusProjeto({
        id: projetoId,
        id_status: 3,
        descricao: descricao.trim(),
      });
      toast.success("Projeto suspenso com sucesso!");
      setDescricao("");
      onSuccess();
      onClose();
    } catch {
      toast.error("Erro ao suspender projeto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <FaPauseCircle style={{ color: "#faad14", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Suspender Projeto</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger
          onClick={handleSuspender}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          Confirmar Suspensão
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Motivo da Suspensão</span>}
          required
        >
          <TextArea
            rows={4}
            placeholder="Descreva detalhadamente o motivo..."
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            size="large"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENTE VISÃO GERAL
───────────────────────────────────────────────────────────────────────────── */
const VisaoGeral = ({ projetoId }) => {
  const navigate = useNavigate();
  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const isDiretor = userStored.diretor === "S";

  const [projeto, setProjeto] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const [suspensoes, setSuspensoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [suspenderModalOpen, setSuspenderModalOpen] = useState(false);

  const id = projetoId; // alias mais semântico

  useEffect(() => {
    if (id) {
      loadDetalhes();
    }
  }, [id]);

  const loadDetalhes = async () => {
    setLoading(true);
    try {
      // Carregar projeto se ainda não estiver disponível
      if (!projeto) {
        const response = await Api.ListProjeto({ id_direcao: userStored.direcao });
        const projetoEncontrado = response.find((p) => p.id === parseInt(id));
        if (projetoEncontrado) {
          setProjeto(projetoEncontrado);
        }
      }
      // Carregar responsáveis e suspensões
      const payload = { id: projeto?.id || id };
      const [resps, susps] = await Promise.all([
        Api.ListResponsavelProjeto(payload).catch(() => []),
        Api.ListSuspensoProjeto(id).catch(() => []),
      ]);
      setResponsaveis(Array.isArray(resps) ? resps : []);
      setSuspensoes(Array.isArray(susps) ? susps : []);
    } catch {
      toast.error("Erro ao carregar dados do projeto.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (novoStatusId) => {
    try {
      setChangingStatus(true);
      await Api.AlterStatusProjeto({
        id: projeto.id,
        id_status: novoStatusId,
      });
      const statusNomes = { 1: "ABERTO", 2: "FECHADO", 3: "SUSPENSO" };
      toast.success(`Projeto ${statusNomes[novoStatusId].toLowerCase()} com sucesso!`);
      setProjeto({ ...projeto, id_status: novoStatusId });
      loadDetalhes();
    } catch {
      toast.error("Erro ao alterar status do projeto.");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleSuspenderSuccess = () => {
    setProjeto({ ...projeto, id_status: 3 });
    loadDetalhes();
  };

  const getStatusTag = (id_status) => {
    const { label, color, icon } = getStatusInfo(id_status);
    return (
      <Tag
        color={color}
        style={{
          borderRadius: 20,
          padding: "4px 12px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {icon} {label}
      </Tag>
    );
  };

  const suspensoesColumns = [
    {
      title: "Motivo",
      dataIndex: "descricao",
      key: "descricao",
      ellipsis: true,
    },
    {
      title: "Entrada",
      dataIndex: "entrada",
      key: "entrada",
      render: (data) => formatDate(data),
    },
    {
      title: "Saída",
      dataIndex: "saida",
      key: "saida",
      render: (data) => formatDate(data),
    },
    {
      title: "Duração",
      key: "duracao",
      render: (_, record) => {
        if (!record.entrada || !record.saida) return "—";
        const entrada = new Date(record.entrada);
        const saida = new Date(record.saida);
        const dias = Math.ceil((saida - entrada) / (1000 * 60 * 60 * 24));
        return `${dias} dia${dias !== 1 ? "s" : ""}`;
      },
    },
  ];

  if (!projeto) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <Spin size="large" />
        <div style={{ marginTop: 24, fontSize: 16, color: "#8c8c8c" }}>
          Carregando informações do projeto...
        </div>
      </div>
    );
  }

  const statusAberto = projeto.id_status === 1;
  const statusSuspenso = projeto.id_status === 3;
  const statusFechado = projeto.id_status === 2;
  const totalDiasSuspenso = calcularDiasSuspenso(suspensoes);
  const progressoProjeto = parseFloat(projeto.progresso) || 0;

  return (
    <Content style={{ padding: "24px 32px" }}>
      <ToastContainer position="top-right" autoClose={4000} />

      {/* Cabeçalho gradiente */}
      <div
        style={{
          background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
          borderRadius: 20,
          padding: "24px 32px",
          marginBottom: 32,
          boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space size="middle">
              <div
                style={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: 16,
                  padding: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaProjectDiagram style={{ fontSize: 36, color: "#fff" }} />
              </div>
              <div>
                <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                  {projeto.titulo}
                </Title>
                <Space size="middle" style={{ marginTop: 8 }}>
                  {getStatusTag(projeto.id_status)}
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Criado em {formatDate(projeto.inicio)}
                  </Text>
                </Space>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              size="large"
              icon={<FaArrowLeft />}
              onClick={() => navigate("/projectos")}
              style={{
                backgroundColor: "#fff",
                color: "#1677ff",
                border: "none",
                fontWeight: 600,
                borderRadius: 30,
                padding: "0 24px",
                height: 48,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              Voltar para Projetos
            </Button>
          </Col>
        </Row>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 20, fontSize: 16, color: "#8c8c8c" }}>
            Carregando dados detalhados...
          </div>
        </div>
      ) : (
        <Row gutter={[24, 24]}>
          {/* Coluna esquerda: descrição, datas, progresso */}
          <Col xs={24} lg={16}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                marginBottom: 24,
              }}
              bodyStyle={{ padding: "24px 28px" }}
              title={
                <Space>
                  <FaProjectDiagram style={{ color: "#1677ff", fontSize: 18 }} />
                  <span style={{ fontWeight: 600, fontSize: 18 }}>Visão Geral</span>
                </Space>
              }
            >
              <Paragraph style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
                {projeto.descricao}
              </Paragraph>

              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar
                      style={{ backgroundColor: "#e6f4ff", color: "#1677ff" }}
                      icon={<FaCalendarAlt />}
                      size={40}
                    />
                    <div>
                      <Text type="secondary">Data de Início</Text>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>
                        {formatDate(projeto.inicio)}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar
                      style={{ backgroundColor: "#fff1f0", color: "#ff4d4f" }}
                      icon={<FaFlagCheckered />}
                      size={40}
                    />
                    <div>
                      <Text type="secondary">Data de Fim</Text>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>
                        {formatDate(projeto.fim)}
                      </div>
                    </div>
                  </div>
                </Col>
                {projeto.datafecho && (
                  <Col span={12}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <Avatar
                        style={{ backgroundColor: "#f6ffed", color: "#52c41a" }}
                        icon={<FaCheckCircle />}
                        size={40}
                      />
                      <div>
                        <Text type="secondary">Data de Fecho</Text>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>
                          {formatDate(projeto.datafecho)}
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
                <Col span={12}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar
                      style={{ backgroundColor: "#fffbe6", color: "#faad14" }}
                      icon={<FaClock />}
                      size={40}
                    />
                    <div>
                      <Text type="secondary">Dias Suspenso</Text>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>
                        {totalDiasSuspenso} dia{totalDiasSuspenso !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Divider style={{ margin: "24px 0 16px" }} />

              <div>
                <Text strong style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
                  Progresso Geral do Projeto
                </Text>
                <Progress
                  percent={progressoProjeto}
                  status={progressoProjeto === 100 ? "success" : "active"}
                  strokeColor={{
                    "0%": "#1677ff",
                    "100%": "#52c41a",
                  }}
                  size={["100%", 20]}
                />
              </div>
            </Card>
          </Col>

          {/* Coluna direita: responsáveis, ações de estado, suspensões */}
          <Col xs={24} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                marginBottom: 24,
              }}
              bodyStyle={{ padding: "24px" }}
              title={
                <Space>
                  <FaUsers style={{ color: "#1677ff", fontSize: 18 }} />
                  <span style={{ fontWeight: 600, fontSize: 18 }}>Responsáveis</span>
                </Space>
              }
            >
              {responsaveis.length === 0 ? (
                <Text type="secondary">Nenhum responsável atribuído.</Text>
              ) : (
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {responsaveis.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <Avatar style={{ backgroundColor: "#722ed1", color: "#fff" }}>
                        {r.nome?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Text strong>{r.nome}</Text>
                    </div>
                  ))}
                </Space>
              )}
            </Card>

            {/* Card de Ações de Estado (apenas para diretor e se projeto não fechado) */}
            {!statusFechado && isDiretor && (
              <Card
                bordered={false}
                style={{
                  borderRadius: 20,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                  marginBottom: 24,
                }}
                bodyStyle={{ padding: "24px" }}
                title={
                  <Space>
                    <FaExclamationCircle style={{ color: "#faad14", fontSize: 18 }} />
                    <span style={{ fontWeight: 600, fontSize: 18 }}>Alterar Estado</span>
                  </Space>
                }
              >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {statusAberto && (
                    <>
                      <Button
                        block
                        size="large"
                        icon={<FaPauseCircle />}
                        onClick={() => setSuspenderModalOpen(true)}
                        loading={changingStatus}
                        style={{
                          borderRadius: 30,
                          height: 48,
                          fontWeight: 500,
                          borderColor: "#faad14",
                          color: "#faad14",
                        }}
                      >
                        Suspender Projeto
                      </Button>
                      <Button
                        block
                        type="primary"
                        size="large"
                        icon={<FaCheckCircle />}
                        onClick={() => handleChangeStatus(2)}
                        loading={changingStatus}
                        style={{
                          borderRadius: 30,
                          height: 48,
                          fontWeight: 500,
                          background: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      >
                        Fechar Projeto
                      </Button>
                    </>
                  )}
                  {statusSuspenso && (
                    <Button
                      block
                      type="primary"
                      size="large"
                      icon={<FaPlayCircle />}
                      onClick={() => handleChangeStatus(1)}
                      loading={changingStatus}
                      style={{
                        borderRadius: 30,
                        height: 48,
                        fontWeight: 500,
                      }}
                    >
                      Reabrir Projeto
                    </Button>
                  )}
                </Space>
              </Card>
            )}

            {/* Histórico de Suspensões */}
            {suspensoes.length > 0 && (
              <Card
                bordered={false}
                style={{
                  borderRadius: 20,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                }}
                bodyStyle={{ padding: 0 }}
                title={
                  <div style={{ padding: "20px 24px 0" }}>
                    <Space>
                      <FaClock style={{ color: "#faad14", fontSize: 18 }} />
                      <span style={{ fontWeight: 600, fontSize: 18 }}>Períodos de Suspensão</span>
                    </Space>
                  </div>
                }
              >
                <Table
                  columns={suspensoesColumns}
                  dataSource={suspensoes}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  style={{ backgroundColor: "#fff" }}
                  locale={{ emptyText: "Nenhuma suspensão registada" }}
                />
              </Card>
            )}
          </Col>
        </Row>
      )}

      {/* Modal de Suspensão */}
      <SuspenderModal
        open={suspenderModalOpen}
        onClose={() => setSuspenderModalOpen(false)}
        onSuccess={handleSuspenderSuccess}
        projetoId={projeto.id}
      />
    </Content>
  );
};

export default VisaoGeral;